import React from "react";
import {
  Compass,
  Users,
  Briefcase,
  GraduationCap,
  Award,
  Sparkles,
} from "lucide-react";
import ServicePageTemplate from "../components/ServicePageTemplate";

const HERO_IMG =
  "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1600&q=80";

const CareerCounselling = () => (
  <ServicePageTemplate
    testId="career-counselling-page"
    slug="cc"
    relatedSlug="career-counselling"
    eyebrow="05 — Career Counselling"
    titleLead="Life after"
    titleItalic="graduation"
    subtitle="Dedicated career counselling for secondary school students preparing for life after graduation — identifying strengths, exploring pathways, and developing the professional skills needed to thrive in a rapidly changing world."
    heroImage={HERO_IMG}
    heroImageAlt="A student at a crossroads — thinking about the path ahead"
    overviewEyebrow="The Approach"
    overviewTitleLead="Strengths first."
    overviewTitleItalic="Then pathways"
    overviewBody={[
      "Most career-counselling conversations begin with what's popular, what pays, or what a family expects. This one begins somewhere earlier: what the young person is actually good at, what genuinely engages them, and what kind of life they want to build.",
      "From that foundation we explore every serious option — four-year universities, community and trade schools, military service, entrepreneurship, apprenticeships, and direct entry to the workforce — without ranking any pathway above the others.",
      "The world of work is changing faster than any generation before. Young people don't just need help picking a major; they need the ability to read the landscape, adapt, and build a working life of substance and meaning.",
    ]}
    whoItServes={[
      {
        title: "9th & 10th Graders",
        description:
          "Early exploration — strengths, interests, and a first look at pathways ahead.",
      },
      {
        title: "11th Graders",
        description:
          "Narrowing options, testing prep, understanding costs, building a shortlist.",
      },
      {
        title: "12th Graders",
        description:
          "Applications, essays, interviews, decision-making, and life-after-June planning.",
      },
      {
        title: "Recent Graduates",
        description:
          "Gap-year decisions, redirection, and skills for entering the workforce.",
      },
    ]}
    receive={[
      "Strengths and interest inventory (structured, not just self-report)",
      "A personalized pathway map with three to five serious options ranked by fit",
      "Guidance on universities, trade schools, military tracks, apprenticeships, and self-employment",
      "Support with applications, essays, résumés, and interview preparation",
      "Financial-literacy basics — scholarships, cost of attendance, ROI conversations",
      "Professional-skills coaching (communication, ownership, digital literacy, work ethic)",
      "Parent debrief sessions so families are aligned throughout the process",
    ]}
    benefits={[
      {
        Icon: Compass,
        title: "Clarity of Direction",
        body: "A clear, considered picture of what to pursue after graduation — not the loudest option in the room.",
      },
      {
        Icon: GraduationCap,
        title: "College Readiness",
        body: "Strong applications, thoughtful essays, and confident interviews for students who choose the university route.",
      },
      {
        Icon: Briefcase,
        title: "Workforce Readiness",
        body: "For students entering work directly — the résumé, communication, and mindset skills employers actually notice.",
      },
      {
        Icon: Award,
        title: "Alternative Pathways",
        body: "Serious exploration of trade schools, military service, entrepreneurship, and apprenticeships — not just as backups.",
      },
      {
        Icon: Sparkles,
        title: "Adaptability",
        body: "How to read a changing economy, learn new skills quickly, and build a career that flexes rather than fractures.",
      },
      {
        Icon: Users,
        title: "Family Alignment",
        body: "Parent debrief sessions so the whole family understands and supports the chosen path.",
      },
    ]}
    faqs={[
      {
        q: "Is this only for students who plan to go to college?",
        a: "No. This is explicitly designed to treat every serious pathway as a legitimate one — universities, trade schools, the military, entrepreneurship, apprenticeships, and direct workforce entry are all explored honestly.",
      },
      {
        q: "When should we start? Is 9th grade too early? Is 12th grade too late?",
        a: "9th grade is a great time to begin with strengths and exploration. 12th grade is not too late — the work simply becomes more focused on application and decision support. Most families begin sometime between 10th and 12th.",
      },
      {
        q: "Do you help with college applications and essays?",
        a: "Yes. Application strategy, essay coaching, résumé preparation, and interview prep are all included as needed.",
      },
      {
        q: "How are parents involved?",
        a: "Every engagement includes parent debrief sessions so the family is aligned. The student remains the primary voice, but decisions are rarely made in a vacuum — the counselling honours that.",
      },
      {
        q: "How long does an engagement typically last?",
        a: "It varies. Some families work with us for a single term to solve a specific decision; others engage across two or three years from exploration through decision. There's no one-size answer.",
      },
    ]}
    finalHeadlineLead="Every graduate deserves"
    finalHeadlineItalic="a real plan"
    finalBody="If your student is standing at the edge of graduation and the path forward feels unclear, the first step is a short discovery call to understand where they are and where they'd like to go."
    primaryCta={{
      label: "Explore Career Counselling",
      kind: "mailto",
      subject: "Career Counselling Inquiry",
    }}
    bookingSubject="Career Counselling Booking"
  />
);

export default CareerCounselling;
